import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, set, update } from 'firebase/database';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheck, FaBriefcase, FaGraduationCap, FaCalendarAlt, FaCopy, FaChartLine } from 'react-icons/fa';

const FranchiseManagement = () => {
    const navigate = useNavigate();
    const [enquiries, setEnquiries] = useState([]);
    const [approvedFranchises, setApprovedFranchises] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('enquiries'); // 'enquiries' or 'approved'

    useEffect(() => {
        if (activeTab === 'enquiries') {
            fetchEnquiries();
        } else {
            fetchApprovedFranchises();
        }
    }, [activeTab]);

    const fetchEnquiries = async () => {
        setLoading(true);
        try {
            const db = getDatabase();
            const applicationsRef = ref(db, 'Matrimony/CareerApplications');
            const snapshot = await get(applicationsRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                const enquiriesArray = Object.entries(data).map(([id, enquiry]) => ({
                    id,
                    ...enquiry
                })).sort((a, b) => b.submittedAt - a.submittedAt);
                setEnquiries(enquiriesArray);
            } else {
                setEnquiries([]);
            }
        } catch (error) {
            console.error('Error fetching enquiries:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchApprovedFranchises = async () => {
        setLoading(true);
        try {
            const db = getDatabase();
            const franchisesRef = ref(db, 'Matrimony/franchises');
            const snapshot = await get(franchisesRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                const franchisesArray = Object.entries(data).map(([id, franchise]) => ({
                    id,
                    ...franchise
                })).sort((a, b) => b.approvedAt - a.approvedAt);
                setApprovedFranchises(franchisesArray);
            } else {
                setApprovedFranchises([]);
            }
        } catch (error) {
            console.error('Error fetching approved franchises:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateReferralCode = (name) => {
        const prefix = name.substring(0, 3).toUpperCase();
        const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase();
        return `${prefix}${randomStr}`;
    };

    const handleApprove = async (enquiry) => {
        if (!window.confirm(`Approve franchise for ${enquiry.fullName}?`)) return;

        setLoading(true);
        try {
            const db = getDatabase();
            const referralCode = generateReferralCode(enquiry.fullName);

            const franchiseData = {
                ...enquiry,
                referralCode,
                status: 'approved',
                approvedAt: Date.now(),
                approvedDate: new Date().toISOString()
            };

            // 1. Save to franchises node
            await set(ref(db, `Matrimony/franchises/${enquiry.mobile}`), franchiseData);

            // 2. Update enquiry status
            await update(ref(db, `Matrimony/CareerApplications/${enquiry.id}`), {
                status: 'approved',
                referralCode
            });

            alert(`Franchise approved!\nReferral Code: ${referralCode}`);
            fetchEnquiries();
        } catch (error) {
            console.error('Error approving franchise:', error);
            alert('Failed to approve franchise');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text, type = 'code') => {
        navigator.clipboard.writeText(text);
        alert(`${type === 'link' ? 'Registration link' : 'Referral code'} copied to clipboard!`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Franchise Management</h1>
                <p style={styles.subtitle}>Review enquiries and manage approved franchises</p>
            </div>

            <div style={styles.tabs}>
                <button
                    onClick={() => setActiveTab('enquiries')}
                    style={{ ...styles.tab, ...(activeTab === 'enquiries' ? styles.activeTab : {}) }}
                >
                    Enquiries ({enquiries.filter(e => e.status === 'pending').length})
                </button>
                <button
                    onClick={() => setActiveTab('approved')}
                    style={{ ...styles.tab, ...(activeTab === 'approved' ? styles.activeTab : {}) }}
                >
                    Approved Franchises ({approvedFranchises.length})
                </button>
            </div>

            <div style={styles.content}>
                {loading && <div style={styles.loading}>Loading...</div>}

                {activeTab === 'enquiries' ? (
                    <div style={styles.grid}>
                        {enquiries.length === 0 ? (
                            <p style={styles.empty}>No enquiries found.</p>
                        ) : (
                            enquiries.map((enquiry) => (
                                <div key={enquiry.id} style={{
                                    ...styles.card,
                                    opacity: enquiry.status === 'approved' ? 0.7 : 1
                                }}>
                                    <div style={styles.cardHeader}>
                                        <h3 style={styles.name}>{enquiry.fullName}</h3>
                                        <span style={{
                                            ...styles.statusBadge,
                                            backgroundColor: enquiry.status === 'approved' ? '#10b981' : '#f59e0b'
                                        }}>
                                            {enquiry.status}
                                        </span>
                                    </div>

                                    <div style={styles.cardBody}>
                                        <div style={styles.infoRow}><FaPhone style={styles.icon} /> {enquiry.mobile}</div>
                                        <div style={styles.infoRow}><FaEnvelope style={styles.icon} /> {enquiry.email}</div>
                                        <div style={styles.infoRow}><FaMapMarkerAlt style={styles.icon} /> {enquiry.city}, {enquiry.state}</div>
                                        <div style={styles.infoRow}><FaBriefcase style={styles.icon} /> Exp: {enquiry.experience}</div>
                                        <div style={styles.infoRow}><FaGraduationCap style={styles.icon} /> {enquiry.qualification}</div>
                                        <div style={styles.infoRow}><FaCalendarAlt style={styles.icon} /> {formatDate(enquiry.submittedDate)}</div>

                                        <div style={styles.planBadge}>{enquiry.selectedPlan}</div>

                                        {enquiry.message && (
                                            <div style={styles.messageBox}>
                                                <strong>Message:</strong>
                                                <p style={styles.messageText}>{enquiry.message}</p>
                                            </div>
                                        )}

                                        {enquiry.address && (
                                            <div style={styles.messageBox}>
                                                <strong>Address:</strong>
                                                <p style={styles.messageText}>{enquiry.address}</p>
                                            </div>
                                        )}
                                    </div>

                                    {enquiry.status === 'pending' ? (
                                        <div style={styles.cardActions}>
                                            <button
                                                onClick={() => handleApprove(enquiry)}
                                                style={styles.approveButton}
                                            >
                                                <FaCheck /> Approve & Generate Code
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={styles.cardActions}>
                                            <button
                                                onClick={() => navigate(`/admin/referrals/${enquiry.referralCode}`)}
                                                style={styles.viewUsersButton}
                                            >
                                                <FaChartLine /> View Referral Report
                                            </button>
                                        </div>
                                    )}

                                    {enquiry.referralCode && (
                                        <div style={styles.codeBox}>
                                            <div style={styles.linkInfo}>
                                                <strong>Registration Link:</strong>
                                                <p style={styles.linkText}>
                                                    {`${window.location.origin}/registration?ref=${enquiry.referralCode}`}
                                                </p>
                                            </div>
                                            <FaCopy
                                                style={styles.copyIcon}
                                                onClick={() => copyToClipboard(`${window.location.origin}/registration?ref=${enquiry.referralCode}`, 'link')}
                                                title="Copy Registration Link"
                                            />
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {approvedFranchises.length === 0 ? (
                            <p style={styles.empty}>No approved franchises found.</p>
                        ) : (
                            approvedFranchises.map((franchise) => (
                                <div key={franchise.id} style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <h3 style={styles.name}>{franchise.fullName}</h3>
                                        <div
                                            style={styles.codeBadge}
                                            onClick={() => copyToClipboard(`${window.location.origin}/registration?ref=${franchise.referralCode}`, 'link')}
                                            title="Copy Registration Link"
                                        >
                                            {franchise.referralCode} <FaCopy size={12} />
                                        </div>
                                    </div>

                                    <div style={styles.cardBody}>
                                        <div style={styles.infoRow}><FaPhone style={styles.icon} /> {franchise.mobile}</div>
                                        <div style={styles.infoRow}><FaMapMarkerAlt style={styles.icon} /> {franchise.city}</div>
                                        <div style={styles.infoRow}><FaCalendarAlt style={styles.icon} /> Approved: {formatDate(franchise.approvedDate)}</div>
                                        <div style={styles.planBadge}>{franchise.selectedPlan}</div>
                                    </div>

                                    <div style={styles.detailsSection}>
                                        <p style={styles.detailItem}><strong>Email:</strong> {franchise.email}</p>
                                        <p style={styles.detailItem}><strong>Exp:</strong> {franchise.experience}</p>
                                        <p style={styles.detailItem}><strong>Qualification:</strong> {franchise.qualification}</p>
                                    </div>

                                    <button
                                        onClick={() => navigate(`/admin/referrals/${franchise.referralCode}`)}
                                        style={styles.viewUsersButton}
                                    >
                                        <FaChartLine /> View Referral Report
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

        </div>
    );
};

const styles = {
    container: {
        padding: '2rem',
        background: '#f7f7f7',
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box'
    },
    header: {
        marginBottom: '2rem',
    },
    title: {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        color: '#1e293b',
        marginBottom: '0.5rem',
    },
    subtitle: {
        fontSize: '1.1rem',
        color: '#64748b',
    },
    tabs: {
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #e2e8f0',
    },
    tab: {
        padding: '1rem 2rem',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#64748b',
        background: 'none',
        border: 'none',
        borderBottom: '3px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
    },
    activeTab: {
        color: '#6a11cb',
        borderBottomColor: '#6a11cb',
    },
    content: {
        position: 'relative',
    },
    loading: {
        textAlign: 'center',
        padding: '2rem',
        fontSize: '1.2rem',
        color: '#6a11cb',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '2rem',
    },
    card: {
        background: 'white',
        borderRadius: '15px',
        padding: '1.5rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f1f5f9',
        paddingBottom: '0.75rem',
    },
    name: {
        fontSize: '1.25rem',
        fontWeight: 'bold',
        color: '#1e293b',
        margin: 0,
    },
    statusBadge: {
        padding: '0.25rem 0.75rem',
        borderRadius: '20px',
        color: 'white',
        fontSize: '0.8degrem',
        fontWeight: 'bold',
        textTransform: 'capitalize',
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        color: '#475569',
        fontSize: '0.95rem',
    },
    icon: {
        color: '#6a11cb',
        width: '16px',
    },
    planBadge: {
        alignSelf: 'flex-start',
        background: '#eef2ff',
        color: '#6366f1',
        padding: '0.4rem 0.8rem',
        borderRadius: '8px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        marginTop: '0.5rem',
    },
    messageBox: {
        background: '#f8fafc',
        padding: '0.75rem',
        borderRadius: '10px',
        marginTop: '0.5rem',
    },
    messageText: {
        fontSize: '0.85rem',
        color: '#475569',
        margin: '0.25rem 0 0 0',
        lineHeight: 1.4,
    },
    cardActions: {
        marginTop: '1rem',
    },
    approveButton: {
        width: '100%',
        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
        color: 'white',
        border: 'none',
        padding: '0.8rem',
        borderRadius: '10px',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'transform 0.2s ease',
    },
    codeBox: {
        marginTop: '1rem',
        background: '#f0fdf4',
        border: '1px dashed #22c55e',
        padding: '0.75rem',
        borderRadius: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#15803d',
        gap: '1rem',
    },
    linkInfo: {
        flex: 1,
        overflow: 'hidden',
    },
    linkText: {
        fontSize: '0.8rem',
        margin: '2px 0 0 0',
        wordBreak: 'break-all',
        color: '#16a34a',
    },
    copyIcon: {
        cursor: 'pointer',
        opacity: 0.7,
    },
    codeBadge: {
        background: '#6a11cb',
        color: 'white',
        padding: '0.4rem 0.8rem',
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    detailsSection: {
        borderTop: '1px solid #f1f5f9',
        paddingTop: '1rem',
        marginTop: '0.5rem',
    },
    detailItem: {
        fontSize: '0.85rem',
        color: '#64748b',
        margin: '0.25rem 0',
    },
    empty: {
        textAlign: 'center',
        gridColumn: '1 / -1',
        padding: '4rem',
        color: '#64748b',
        fontSize: '1.2rem',
    },
    viewUsersButton: {
        marginTop: '1rem',
        background: '#eff6ff',
        color: '#2563eb',
        border: '1px solid #bfdbfe',
        padding: '0.6rem',
        borderRadius: '8px',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'all 0.2s ease',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
    },
    modal: {
        background: 'white',
        borderRadius: '15px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    },
    modalHeader: {
        padding: '1.5rem',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#1e293b',
    },
    modalContent: {
        padding: '1.5rem',
        overflowY: 'auto',
    },
    closeIcon: {
        cursor: 'pointer',
        fontSize: '1.2rem',
        color: '#64748b',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        textAlign: 'left',
    },
    th: {
        padding: '0.75rem',
        borderBottom: '2px solid #f1f5f9',
        color: '#64748b',
        fontSize: '0.9rem',
    },
    td: {
        padding: '0.75rem',
        borderBottom: '1px solid #f1f5f9',
        color: '#334155',
        fontSize: '0.9rem',
    },
};

export default FranchiseManagement;
